/**
 * AI CONTEXT: ui-consistency - Footer with brand, links, and social
 * 
 * Purpose:
 * - Provide navigational quick links and legal entry points
 * - Reinforce brand identity with consistent styling
 * - Maintain mobile-friendly layout
 * 
 * Future considerations:
 * - Pull links from config/CMS
 * - Add newsletter signup
 * - Add trust badges/compliance marks
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { t } from '../../i18n';
import { Shield, Facebook, Instagram, Twitter } from 'lucide-react';

const Footer = () => {
  return (
    <footer className="bg-theme-black text-theme-silver">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2">
            <div className="flex items-center space-x-2 mb-4">
              <div className="glass-card-dark p-2 rounded-full">
                <Shield className="h-8 w-8 text-theme-bronze" />
              </div>
              <span className="text-xl font-bold text-theme-silver">{t('brand.name')}</span>
            </div>
            <p className="text-theme-silver-light mb-4 max-w-md">
              Premium tactical gear and exclusive giveaways. Join our community for the best in tactical equipment and exciting sweepstakes opportunities.
            </p>
            <div className="flex space-x-4">
              <a href="https://facebook.com/tolberttactical" target="_blank" rel="noopener noreferrer" className="glass-card-dark p-2 rounded-full text-theme-silver-light hover:text-theme-bronze transition-all duration-300 hover:shadow-glow">
                <Facebook className="h-5 w-5" />
              </a>
              <a href="https://instagram.com/tolberttactical" target="_blank" rel="noopener noreferrer" className="glass-card-dark p-2 rounded-full text-theme-silver-light hover:text-theme-bronze transition-all duration-300 hover:shadow-glow">
                <Instagram className="h-5 w-5" />
              </a>
              <a href="https://twitter.com/tolberttactical" target="_blank" rel="noopener noreferrer" className="glass-card-dark p-2 rounded-full text-theme-silver-light hover:text-theme-bronze transition-all duration-300 hover:shadow-glow">
                <Twitter className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-theme-silver">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/store" className="text-theme-silver-light hover:text-theme-silver transition-all duration-300 hover:bg-glass-white/10 px-2 py-1 rounded-lg">
                  {t('nav.store')}
                </Link>
              </li>
              <li>
                <Link to="/giveaways" className="text-theme-silver-light hover:text-theme-silver transition-all duration-300 hover:bg-glass-white/10 px-2 py-1 rounded-lg">
                  {t('nav.giveaways')}
                </Link>
              </li>
              <li>
                <Link to="/acquisition" className="text-theme-silver-light hover:text-theme-silver transition-all duration-300 hover:bg-glass-white/10 px-2 py-1 rounded-lg">
                  {t('nav.acquire')}
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-theme-silver-light hover:text-theme-silver transition-all duration-300 hover:bg-glass-white/10 px-2 py-1 rounded-lg">
                  {t('nav.blog')}
                </Link>
              </li>
              <li>
                <Link to="/dashboard" className="text-theme-silver-light hover:text-theme-silver transition-all duration-300 hover:bg-glass-white/10 px-2 py-1 rounded-lg">
                  {t('nav.dashboard')}
                </Link>
              </li>
              <li>
                <Link to="/login" className="text-theme-silver-light hover:text-theme-silver transition-all duration-300 hover:bg-glass-white/10 px-2 py-1 rounded-lg">
                  {t('auth.signin')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-theme-silver">Legal</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/legal" className="text-theme-silver-light hover:text-theme-silver transition-all duration-300 hover:bg-glass-white/10 px-2 py-1 rounded-lg">
                  Terms & Conditions
                </Link>
              </li>
              <li>
                <Link to="/legal" className="text-theme-silver-light hover:text-theme-silver transition-all duration-300 hover:bg-glass-white/10 px-2 py-1 rounded-lg">
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link to="/age-verification" className="text-theme-silver-light hover:text-theme-silver transition-all duration-300 hover:bg-glass-white/10 px-2 py-1 rounded-lg">
                  Age Verification
                </Link>
              </li>
              <li>
                <a href="/legal#sweepstakes" className="text-theme-silver-light hover:text-theme-silver transition-all duration-300 hover:bg-glass-white/10 px-2 py-1 rounded-lg">
                  Sweepstakes Rules
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-theme-bronze/30 mt-8 pt-8 space-y-6">
          <div className="text-theme-silver-light text-xs sm:text-sm leading-relaxed max-w-5xl">
            <p className="font-semibold text-theme-silver mb-2">⚠ LEGAL DISCLAIMER</p>
            <p>
              All promotion drawings conducted by Tolbert Tactical are random and determined solely by chance. Participation in any promotion does not guarantee a win, prize, or any specific outcome. Entry fees are non-refundable under any circumstances. Odds of winning vary based on the total number of entries sold for each drawing. All results are final and binding. Tolbert Tactical, its organizers, agents, and affiliates are not liable for any direct or indirect loss, damage, or claim arising from participation. By entering any promotion, participants acknowledge and accept these terms and agree to enter and play entirely at their own risk. Participation is subject to your state&apos;s and local laws governing firearms. It is your responsibility to familiarize yourself with and comply with all applicable local, state, and federal laws before entering. Age requirements vary by prize type — handgun promotions require participants to be 21 years of age or older under federal law; long gun promotions require participants to be 18 years of age or older; other prizes may carry different age restrictions. Compliance with all applicable laws will be verified by Tolbert Tactical upon transfer of any firearm. Must provide valid government-issued ID upon prize transfer.
            </p>
          </div>
          <div className="text-theme-silver-light text-xs sm:text-sm leading-relaxed">
            <p className="font-semibold text-theme-silver mb-2">🔰 FEDERAL FIREARMS LICENSE</p>
            <p>FFL # 9-87-08240</p>
            <p>Utah-Licensed Federal Firearms Dealer · Transfers Facilitated Nationwide</p>
            <p>License Expires: Sep 01, 2028</p>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-theme-bronze/30 mt-8 pt-8 pb-20 md:pb-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-theme-silver-light text-sm">
              © 2024 {t('brand.name')}. All rights reserved.
            </p>
            <div className="mt-4 md:mt-0">
              <p className="text-theme-silver-light text-sm">
                v1.1 TT App Engine, Developed by{' '}
                <a 
                  href="https://stevenhooley.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-theme-bronze hover:text-theme-silver transition-all duration-300 hover:underline"
                >
                  Steven Hooley
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 